import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md';
}

const variantStyles = {
  default: 'bg-gray-100 text-gray-700',
  primary: 'bg-primary-100 text-primary-700',
  secondary: 'bg-gray-100 text-gray-600',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  danger: 'bg-red-100 text-red-700',
};

const sizeStyles = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-xs',
};

export function Badge({
  className,
  variant = 'default',
  size = 'md',
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={twMerge(
        clsx(
          'inline-flex items-center rounded-full font-medium',
          variantStyles[variant],
          sizeStyles[size],
          className
        )
      )}
      {...props}
    >
      {children}
    </span>
  );
}

// Priority-specific badges
export function PriorityBadge({ priority }: { priority: 'critical' | 'high' | 'medium' | 'low' | 'future' }) {
  const styles: Record<string, string> = {
    critical: 'bg-red-200 text-red-800',
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700',
    future: 'bg-gray-100 text-gray-600',
  };

  const labels: Record<string, string> = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    future: 'Future',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        styles[priority]
      )}
    >
      {labels[priority]}
    </span>
  );
}

// Category badge for expenses/transactions
// Supports both legacy hardcoded categories and dynamic category names
export function CategoryBadge({
  category,
  label,
}: {
  category: string;
  label?: string;
}) {
  const styles: Record<string, string> = {
    food: 'bg-orange-100 text-orange-700',
    transport: 'bg-blue-100 text-blue-700',
    shopping: 'bg-pink-100 text-pink-700',
    entertainment: 'bg-purple-100 text-purple-700',
    utilities: 'bg-gray-100 text-gray-700',
    healthcare: 'bg-red-100 text-red-700',
    education: 'bg-indigo-100 text-indigo-700',
    travel: 'bg-teal-100 text-teal-700',
    other: 'bg-gray-100 text-gray-600',
    None: 'bg-gray-50 text-gray-400 italic',
  };

  const labels: Record<string, string> = {
    food: 'Food & Dining',
    transport: 'Transportation',
    shopping: 'Shopping',
    entertainment: 'Entertainment',
    utilities: 'Utilities',
    healthcare: 'Healthcare',
    education: 'Education',
    travel: 'Travel',
    other: 'Other',
    None: 'None',
  };

  const displayLabel = label || labels[category] || category;
  const style = styles[category] || 'bg-gray-100 text-gray-600';

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        style
      )}
    >
      {displayLabel}
    </span>
  );
}

